/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import * as H from 'history';
import { useParams } from 'react-router-dom';
import { ViewModel } from '../../../../../../core/viewmodel/ViewModel';
import { EntryService } from '../../../../../../services/EntryService';
import { AuthStore } from '../../../../../../stores/application/AuthStore';
import { SnackbarStore } from '../../../../../../stores/application/SnackbarStore';
import { EntriesReviewStore } from '../../../../../../stores/components/EntriesReviewStore';
import { StoreProvider } from '../../../../../../stores/StoreProvider';
import { ChangeEvent } from 'react';
import { Entry, ENTRY_REVIEW_STATE } from '../../../../../../types/course/Entry';
import { getDateTime } from '../../../../../../utils/entry';

export class EntryContentViewModel extends ViewModel {
	private readonly entryService: EntryService;
	private readonly authStore: AuthStore;
	private readonly snackbarStore: SnackbarStore;
	private readonly entriesReviewStore: EntriesReviewStore;

	private routerParams: { courseId?: string } = useParams();
	private courseId = this.routerParams.courseId;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.entryService = storeProvider.entryService;
		this.authStore = storeProvider.authStore;
		this.snackbarStore = storeProvider.snackbarStore;
		this.entriesReviewStore = storeProvider.entriesReviewStore;
		this.queryFeedback();
	}

	public closeDetailView(): void {
		this.entriesReviewStore.closeDetailView();
	}

	public isPublishingView(): boolean {
		return this.entriesReviewStore.isPublishingView();
	}

	public setIsPublishingView(bool: boolean): void {
		this.entriesReviewStore.setIsPublishingView(bool);
	}

	public queryFeedback(): void {
		const entry = this.entriesReviewStore.getEntry();
		if (entry) {
			this.setFeedback(entry.feedback);
		}
	}

	public setFeedback(feedback: string): void {
		this.entriesReviewStore.setFeedback(feedback);
	}

	public getFeedback(): string {
		return this.entriesReviewStore.getFeedback();
	}

	public onFeedbackChange(event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void {
		const text = event.target.value;
		this.setFeedback(text);
	}

	public getStudentImgUrl(imageUrl: string): string {
		let url = imageUrl;

		if (!imageUrl || imageUrl === '') {
			url = `${process.env.PUBLIC_URL}/img/profile.svg`;
		}
		return url;
	}

	public updateReviewState(entryId: string, reviewState: ENTRY_REVIEW_STATE, entryState: ENTRY_REVIEW_STATE): void {
		const authToken = this.authStore.getAuthToken();
		const feedback = this.getFeedback();

		this.entryService
			.updateReviewState(entryId, reviewState, authToken, feedback)
			.then(() => {
				if (entryState === ENTRY_REVIEW_STATE.REJECTED) {
					// entryState is rejected and will be approved
					this.queryRejectedEntries(this.routerParams.courseId);
					this.queryNumberOfPagesRejected();
					this.queryApprovedEntries(this.routerParams.courseId);
					this.queryNumberOfPagesApproved();
				} else {
					this.queryNewEntries(this.routerParams.courseId);
					this.queryNumberOfPagesNewEntries();
					if (reviewState === ENTRY_REVIEW_STATE.APPROVED) {
						this.queryApprovedEntries(this.routerParams.courseId);
						this.queryNumberOfPagesApproved();
					} else {
						this.queryRejectedEntries(this.routerParams.courseId);
						this.queryNumberOfPagesRejected();
					}
				}
				this.closeDetailView();
			})
			.catch(() => {
				this.snackbarStore.showError('entries.update_review_error');
			});
	}

	private queryNewEntries(courseId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();
		const pageNumber = this.entriesReviewStore.getPageNumberNewEntries();

		return this.entryService
			.getNewEntries(courseId, authToken, pageNumber)
			.then((entries: Entry[]) => {
				this.entriesReviewStore.setNewEntries(entries);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.query_error');
			});
	}

	private queryApprovedEntries(courseId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();
		const pageNumber = this.entriesReviewStore.getPageNumberApproved();

		return this.entryService
			.getApprovedEntries(courseId, authToken, pageNumber)
			.then((entries: Entry[]) => {
				this.entriesReviewStore.setApprovedEntries(entries);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.query_error');
			});
	}

	private queryRejectedEntries(courseId: string): Promise<void> | void {
		const authToken: string = this.authStore.getAuthToken();
		const pageNumber = this.entriesReviewStore.getPageNumberRejected();

		return this.entryService
			.getRejectedEntries(courseId, authToken, pageNumber)
			.then((entries: Entry[]) => {
				this.entriesReviewStore.setRejectedEntries(entries);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.query_error');
			});
	}

	private async queryNumberOfPagesNewEntries(): Promise<void> {
		const authToken: string = this.authStore.getAuthToken();
		const courseId = this.routerParams.courseId;

		return this.entryService
			.getNumberOfPagesNewEntries(courseId, authToken)
			.then((numberOfPages: number) => {
				this.entriesReviewStore.setNumberOfPagesNewEntries(numberOfPages);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.number_of_pages_query_error');
			});
	}

	private async queryNumberOfPagesApproved(): Promise<void> {
		const authToken: string = this.authStore.getAuthToken();
		const courseId = this.routerParams.courseId;

		return this.entryService
			.getNumberOfPagesApproved(courseId, authToken)
			.then((numberOfPages: number) => {
				this.entriesReviewStore.setNumberOfPagesApproved(numberOfPages);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.number_of_pages_query_error');
			});
	}

	private async queryNumberOfPagesRejected(): Promise<void> {
		const authToken: string = this.authStore.getAuthToken();
		const courseId = this.routerParams.courseId;

		return this.entryService
			.getNumberOfPagesRejected(courseId, authToken)
			.then((numberOfPages: number) => {
				this.entriesReviewStore.setNumberOfPagesRejected(numberOfPages);
			})
			.catch(() => {
				this.snackbarStore.showError('entries.number_of_pages_query_error');
			});
	}

	public redirectToMaps(lat: number, long: number): void {
		window.open(`https://www.google.de/maps/search/?api=1&query=${lat},${long}`, '_blank');
	}

	public isValidData(): boolean {
		return this.entriesReviewStore.isValidData();
	}

	public toggleIsValidData(): void {
		this.entriesReviewStore.setIsValidData(!this.entriesReviewStore.isValidData());
	}

	public getDateString(isoString: string): string {
		const date = new Date(isoString);
		return getDateTime(date);
	}

	public requestPublication(entryId: string): void {
		const authToken: string = this.authStore.getAuthToken();

		this.entryService
			.requestPublication(entryId, authToken)
			.then(() => {
				this.closeDetailView();
				this.snackbarStore.showSuccess('entries.publication_success');
			})
			.catch(() => {
				this.snackbarStore.showError('entries.publication_error');
			});
	}
}
